import { useState, useCallback, useRef, type ReactNode, type RefObject, type ChangeEvent } from 'react'
import { getDroppedFiles } from '@uppy/utils'
import { GlobalToastType, kToastDurationLongMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { kNoUploadableFilesMessage } from '@/user_files/drive/constants'
import { getAcceptedFilesList } from '@/constants/constants-gcs'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'

type DriveDropzoneProps = {
  /** The content to render inside the dropzone. This is the content of the DriveView. */
  children: ReactNode
  /** Whether the dropzone is disabled. */
  disabled?: boolean
  /** Ref from `useDriveUpload` — attaches to the hidden file input. */
  fileInputRef: RefObject<HTMLInputElement>
  /** Ref from `useDriveUpload` — attaches to the hidden directory-picker input. */
  folderInputRef: RefObject<HTMLInputElement>
  /** Adds files to the upload queue. */
  addFiles: (files: File[]) => void
  /** When the user picks file(s) or a folder via the OS dialog. */
  onFilesPicked: (e: ChangeEvent<HTMLInputElement>) => void
}

/**
 * Full-screen drag-and-drop overlay for file uploads.
 * Wraps DriveView content and shows an overlay when files are dragged over.
 * Similar to Google Drive's drop behavior.
 */
export function DriveDropzone({ children, disabled = false, fileInputRef, folderInputRef, addFiles, onFilesPicked }: DriveDropzoneProps) {
  const { enabled: emailFileProcessingEnabled } = useRuntimeFeatureFlag(RuntimeFeatureFlag.EMAIL_FILE_PROCESSING)
  const acceptedFileTypes = Object.values(getAcceptedFilesList(emailFileProcessingEnabled)).flat().join(',')
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = useRef(0) // Handle nested elements
  const dispatch = useAppDispatch()

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return
      onFilesPicked(e)
    },
    [disabled, onFilesPicked]
  )

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounterRef.current++

      // Only show overlay if files are being dragged and not disabled
      if (!disabled && e.dataTransfer.types.includes('Files')) {
        setIsDragOver(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--

    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setIsDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const { dataTransfer } = e
      dragCounterRef.current = 0
      setIsDragOver(false)

      if (disabled) return

      const showDropError = (error: unknown) => {
        console.error('Drop read error:', error)
        dispatch(
          openGlobalToast({
            type: GlobalToastType.ERROR,
            message: 'File could not be uploaded.',
            durationMs: kToastDurationLongMs,
          })
        )
      }

      try {
        const files = await getDroppedFiles(dataTransfer, { logDropError: showDropError })
        // An empty dropped folder walks in as zero files, so nothing downstream ever sees the
        // drop — say so here rather than letting it look like the drop was ignored.
        if (files.length === 0) {
          dispatch(openGlobalToast({ type: GlobalToastType.WARNING, message: kNoUploadableFilesMessage, durationMs: kToastDurationMediumMs }))
          return
        }
        addFiles(files)
      } catch (error) {
        showDropError(error)
      }
    },
    [disabled, dispatch, addFiles]
  )

  return (
    <div
      className="relative flex flex-col flex-1 min-h-0"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input ref={fileInputRef} type="file" multiple accept={acceptedFileTypes} disabled={disabled} className="hidden" onChange={handleInputChange} />
      {/*
        A separate input rather than toggling `webkitdirectory` on the one above: flipping the
        attribute and calling click() in the same tick races React's commit, and a second hidden
        input costs nothing. `webkitdirectory` is not in React's typed attribute list, so it is
        spread in. `multiple` is harmless here — some browsers honour multi-directory selection —
        but nothing relies on it.
      */}
      <input ref={folderInputRef} type="file" multiple disabled={disabled} className="hidden" onChange={handleInputChange} {...{ webkitdirectory: '' }} />
      {children}

      {/* Full-screen overlay when dragging */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-brand-500/10 border-dashed border-brand-400 rounded-t-[6px] pointer-events-none border-t-[1.5px] border-l-[1.5px] border-r-[1.5px]" />
      )}
    </div>
  )
}
